import { ChevronDown, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { Button } from "@/shared/ui/Button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/ui/DropDownMenu";
import { Input } from "@/shared/ui/Input";
import { Popover, PopoverAnchor, PopoverContent } from "@/shared/ui/Popover";
import {
  formatCurrencyField,
  normalizeSearchText,
  normalizeCurrencyTyping,
} from "../movement-allocations/utils";
import SettlementLinkedMovement from "./SettlementLinkedMovement";
import type { EditableSettlementState } from "./useSettlementDrawerState";
import type { BankMovement } from "../../core/models/admin";
import type { BankMovementAccount, Settlement } from "../../types/accounting.types";

type SettlementEditorProps = {
  settlement: Settlement | null
  settlementDraft: EditableSettlementState | null
  setSettlementField: (field: keyof EditableSettlementState, value: string) => void
  isSettlementBusy: boolean
  canSaveSettlement: boolean
  // Account popover
  accountPopoverOpen: boolean
  setAccountPopoverOpen: (open: boolean) => void
  accountSearch: string
  setAccountSearch: (value: string) => void
  bankAccounts: BankMovementAccount[]
  isBankAccountsLoading: boolean
  selectedAccount: BankMovementAccount | null
  // Payment method popover
  paymentMethodPopoverOpen: boolean
  setPaymentMethodPopoverOpen: (open: boolean) => void
  paymentMethodSearch: string
  setPaymentMethodSearch: (value: string) => void
  filteredPaymentMethods: string[]
  isPaymentMethodsLoading: boolean
  // Actions
  onSaveSettlement: () => void
  onDeleteSettlement: () => void
  isDeleteBusy: boolean
  // Linked movement
  linkedMovement: BankMovement | null
  isLoadingMovement: boolean
  movementSearchQuery: string
  onMovementSearchChange: (value: string) => void
  movementSearchResults: BankMovement[]
  isSearchingMovement: boolean
  attachingMovementId: string | null
  isDetachingMovement: boolean
  onAttachMovement: (movementId: string) => void
  onDetachMovement: () => void
  onOpenMovement: (movementId: string) => void
  // Allocation actions
  isBusy: boolean
  isCreatePending: boolean
  onAddAllocation: (type: "simple" | "activity" | "stock") => void
  onOpenRefundSearch: () => void
  onOpenEcommerceSearch: () => void
};

const SettlementEditor = ({
  settlement,
  settlementDraft,
  setSettlementField,
  isSettlementBusy,
  canSaveSettlement,
  accountPopoverOpen,
  setAccountPopoverOpen,
  accountSearch,
  setAccountSearch,
  bankAccounts,
  isBankAccountsLoading,
  selectedAccount,
  paymentMethodPopoverOpen,
  setPaymentMethodPopoverOpen,
  paymentMethodSearch,
  setPaymentMethodSearch,
  filteredPaymentMethods,
  isPaymentMethodsLoading,
  onSaveSettlement,
  onDeleteSettlement,
  isDeleteBusy,
  linkedMovement,
  isLoadingMovement,
  movementSearchQuery,
  onMovementSearchChange,
  movementSearchResults,
  isSearchingMovement,
  attachingMovementId,
  isDetachingMovement,
  onAttachMovement,
  onDetachMovement,
  onOpenMovement,
  isBusy,
  isCreatePending,
  onAddAllocation,
  onOpenRefundSearch,
  onOpenEcommerceSearch,
}: SettlementEditorProps) => {
  const filteredBankAccounts = useMemo(() => {
    const query = normalizeSearchText(accountSearch);
    if (!query) return bankAccounts;
    return bankAccounts.filter((account) => normalizeSearchText(account.name).includes(query));
  }, [accountSearch, bankAccounts]);

  return (
    <div className="rounded-md border bg-white p-3 mb-4 space-y-3 [&_label]:!text-[12px] [&_label]:font-normal [&_label]:leading-tight [&_input]:!text-[12px] [&_input]:md:!text-[12px] [&_input]:leading-tight">
      <div className="space-y-2.5">
        {/* Row 1: Date, Émetteur, Numéro */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-2">
          <div className="space-y-1 xl:col-span-2">
            <label className="text-[12px] leading-tight text-slate-600">Date</label>
            <Input
              type="date"
              min={ACCOUNTING_DATE_BOUNDS.min}
              max={ACCOUNTING_DATE_BOUNDS.max}
              value={settlementDraft?.date ?? ""}
              onChange={(event) => setSettlementField("date", event.target.value)}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight"
              disabled={isSettlementBusy || !settlementDraft}
            />
          </div>
          <div className="space-y-1 xl:col-span-6">
            <label className="text-[12px] leading-tight text-slate-600">Émetteur</label>
            <Input
              value={settlementDraft?.issuer ?? ""}
              onChange={(event) => setSettlementField("issuer", event.target.value)}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight"
              disabled={isSettlementBusy || !settlementDraft}
            />
          </div>
          <div className="space-y-1 xl:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Numéro</label>
            <Input
              value={settlementDraft?.number ?? ""}
              onChange={(event) => setSettlementField("number", event.target.value)}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight"
              disabled={isSettlementBusy || !settlementDraft}
            />
          </div>
        </div>

        {/* Row 2: Mode de règlement, Montant, Enc. différé */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-2">
          <div className="space-y-1 xl:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Mode de règlement</label>
            <Popover open={paymentMethodPopoverOpen} onOpenChange={setPaymentMethodPopoverOpen}>
              <PopoverAnchor asChild>
                <div className="relative">
                  <Input
                    value={paymentMethodPopoverOpen ? paymentMethodSearch : (settlementDraft?.paymentMethod || "")}
                    onFocus={() => {
                      setPaymentMethodSearch("");
                      setPaymentMethodPopoverOpen(true);
                    }}
                    onClick={() => setPaymentMethodPopoverOpen(true)}
                    onChange={(event) => {
                      setPaymentMethodSearch(event.target.value);
                      setPaymentMethodPopoverOpen(true);
                    }}
                    placeholder={isPaymentMethodsLoading ? "Chargement..." : "Rechercher un mode"}
                    className="h-8 !text-[12px] md:!text-[12px] leading-tight pr-8"
                    disabled={isSettlementBusy || !settlementDraft || isPaymentMethodsLoading}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">▼</span>
                </div>
              </PopoverAnchor>
              <PopoverContent align="start" className="w-[360px] p-1">
                <div
                  className="max-h-52 overflow-y-auto overscroll-contain [touch-action:pan-y]"
                  onWheelCapture={(event) => event.stopPropagation()}
                >
                  {filteredPaymentMethods.length === 0 && (
                    <div className="px-2 py-1.5 text-[12px] text-slate-500">Aucun mode trouvé</div>
                  )}
                  {filteredPaymentMethods.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-[12px] rounded hover:bg-slate-100"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSettlementField("paymentMethod", mode);
                        setPaymentMethodSearch("");
                        setPaymentMethodPopoverOpen(false);
                      }}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1 xl:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Montant</label>
            <Input
              inputMode="decimal"
              value={settlementDraft?.amount ?? ""}
              onChange={(event) => setSettlementField("amount", normalizeCurrencyTyping(event.target.value))}
              onBlur={(event) => setSettlementField("amount", formatCurrencyField(event.target.value))}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right"
              placeholder="0,00"
              disabled={isSettlementBusy || !settlementDraft}
            />
          </div>
          <div className="space-y-1 xl:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Enc. différé</label>
            <Input
              type="date"
              min={ACCOUNTING_DATE_BOUNDS.min}
              max={ACCOUNTING_DATE_BOUNDS.max}
              value={settlementDraft?.deferredDate ?? ""}
              onChange={(event) => setSettlementField("deferredDate", event.target.value)}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight"
              disabled={isSettlementBusy || !settlementDraft}
            />
          </div>
        </div>

        {/* Row 3: Compte, Bénéficiaire, Statut */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-2">
          <div className="space-y-1 xl:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Compte</label>
            <Popover open={accountPopoverOpen} onOpenChange={setAccountPopoverOpen}>
              <PopoverAnchor asChild>
                <div className="relative">
                  <Input
                    value={accountPopoverOpen ? accountSearch : (selectedAccount?.name || settlement?.bankAccount?.name || "")}
                    onFocus={() => {
                      setAccountSearch("");
                      setAccountPopoverOpen(true);
                    }}
                    onClick={() => setAccountPopoverOpen(true)}
                    onChange={(event) => {
                      setAccountSearch(event.target.value);
                      setAccountPopoverOpen(true);
                    }}
                    placeholder={isBankAccountsLoading ? "Chargement..." : "Rechercher un compte"}
                    className="h-8 !text-[12px] md:!text-[12px] leading-tight pr-8"
                    disabled={isSettlementBusy || !settlementDraft || isBankAccountsLoading}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">▼</span>
                </div>
              </PopoverAnchor>
              <PopoverContent align="start" className="w-[360px] p-1">
                <div className="max-h-52 overflow-y-auto">
                  {filteredBankAccounts.length === 0 && (
                    <div className="px-2 py-1.5 text-[12px] text-slate-500">Aucun compte trouvé</div>
                  )}
                  {filteredBankAccounts.map((account) => (
                    <button
                      key={account.id}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-[12px] rounded hover:bg-slate-100"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSettlementField("bankAccountId", account.id);
                        setAccountSearch("");
                        setAccountPopoverOpen(false);
                      }}
                    >
                      {account.name}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1 xl:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Bénéficiaire</label>
            <Input
              value={settlement?.clientName ?? ""}
              className="h-8 !text-[12px] md:!text-[12px] leading-tight bg-slate-50"
              disabled
            />
          </div>
          <div className="space-y-1 xl:col-span-4">
            <label className="text-[12px] leading-tight text-slate-600">Statut</label>
            <div className="flex items-center h-8">
              <span
                className={[
                  "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                  settlement?.deposited
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200",
                ].join(" ")}
              >
                {settlement?.deposited ? "Remis" : "Non remis"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Linked bank movement */}
      <SettlementLinkedMovement
        linkedMovement={linkedMovement}
        isLoadingMovement={isLoadingMovement}
        searchQuery={movementSearchQuery}
        onSearchChange={onMovementSearchChange}
        searchResults={movementSearchResults}
        isSearching={isSearchingMovement}
        attachingId={attachingMovementId}
        isDetaching={isDetachingMovement}
        onAttach={onAttachMovement}
        onDetach={onDetachMovement}
        onOpenMovement={onOpenMovement}
      />

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onDeleteSettlement}
          disabled={isDeleteBusy || isSettlementBusy}
          className="h-8 px-3 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
        >
          Supprimer le règlement
        </Button>
        <Button
          type="button"
          variant={canSaveSettlement ? "success" : "outline"}
          onClick={onSaveSettlement}
          disabled={!settlement || isSettlementBusy || !canSaveSettlement}
          className="h-8 px-3 text-xs"
        >
          {isSettlementBusy
            ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Enregistrement...
              </span>
            )
            : "Enregistrer le règlement"}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={isBusy || isCreatePending || isSettlementBusy}
              className="h-8 px-3 text-xs"
            >
              {isCreatePending
                ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Ajout...
                  </span>
                )
                : (
                  <span className="inline-flex items-center gap-1.5">
                    Ajouter une affectation
                    <ChevronDown className="h-3.5 w-3.5" />
                  </span>
                )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onAddAllocation("simple")}>
              Écriture simple
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onOpenEcommerceSearch()}>
              Écriture &quot;Bons plans&quot;
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onOpenRefundSearch()}>
              Écriture remboursement
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onAddAllocation("stock")}>
              Écriture stock
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default SettlementEditor;
